import "vite/modulepreload-polyfill";
// Popover API polyfill for the combobox dropdown (and any native popover) on
// browsers below Baseline 2024. Self-installs, feature-detected: a no-op where
// popover is native. Imported explicitly so it does not depend on
// @tailwindplus/elements (which also bundles it) staying in the bundle.
import "@oddbird/popover-polyfill";
import "@tailwindplus/elements";
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import topbar from "topbar";
import { hooks as colocatedHooks } from "phoenix-colocated/gt";
import CSVboxImporter from "./hooks/csvbox_importer";
import IntegrationAppImporter from "./hooks/integration_app_importer";
import S3ChunkedUploader from "./hooks/s3_chunked_uploader";

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

const requestId = document.querySelector("meta[name='x-request-id']")?.getAttribute("content");

// Copies each attribute in `names` from `fromEl` to `toEl` when present on `fromEl`.
function copyAttributes(fromEl, toEl, names) {
  for (const name of names) {
    if (fromEl.hasAttribute(name)) toEl.setAttribute(name, fromEl.getAttribute(name));
  }
}

const liveSocket = new LiveSocket("/live", Socket, {
  hooks: { ...colocatedHooks, CSVboxImporter, IntegrationAppImporter, S3ChunkedUploader },
  uploaders: { S3: S3ChunkedUploader.S3 },
  // currently disabled
  longPollFallbackMs: null,
  // A deploy drains every socket on a pod. Jitter spreads the reconnects across the
  // remaining pods instead of all clients retrying on the same 10ms, 50ms, ... schedule.
  reconnectAfterMs: tries => ([100, 500, 1000, 2000, 5000][tries - 1] || 10000) * (0.5 + Math.random()),
  // Both disconnect toasts ("can't find the internet" and "Something went wrong!") wait 3s, so a
  // reconnect within that window (a deploy) only shows topbar.
  disconnectedTimeout: 3000,
  params: {
    _csrf_token: csrfToken,
    request_id: requestId,
  },
  dom: {
    // TLDR: keeps the drawer, and the filter popover, "open" across LiveView patches
    // @tailwindplus/elements (<el-dialog>, <el-popover>) imperatively set
    // runtime attributes outside of any server render: `open` on
    // <el-dialog>'s inner native <dialog> via showModal(); `open`, `style`
    // (anchor positioning) and `aria-labelledby` on <el-popover> itself;
    // `aria-expanded` on the button[popovertarget] that opens it. The
    // server never renders any of these, so morphdom would strip them on
    // every re-render (initial hydration, phx-change, any patch), tearing
    // down the widget's top-layer state and closing it mid-interaction.
    // Copying them from the live DOM onto the incoming element keeps the
    // trees aligned and leaves the widget's runtime state untouched.
    // TODO: Review it after better support for WebComponents lands on LiveView
    onBeforeElUpdated(fromEl, toEl) {
      if (
        fromEl.tagName === "DIALOG" &&
        fromEl.parentElement?.tagName?.toLowerCase() === "el-dialog" &&
        fromEl.hasAttribute("open")
      ) {
        copyAttributes(fromEl, toEl, ["open"]);
      }

      if (fromEl.tagName.toLowerCase() === "el-popover" && fromEl.hasAttribute("open")) {
        copyAttributes(fromEl, toEl, ["open", "style", "aria-labelledby"]);
      }

      if (fromEl.tagName === "BUTTON" && fromEl.hasAttribute("popovertarget") && fromEl.hasAttribute("aria-expanded")) {
        copyAttributes(fromEl, toEl, ["aria-expanded"]);
      }
    },
  },
});

topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" });
window.addEventListener("phx:page-loading-start", _info => topbar.show(300));
window.addEventListener("phx:page-loading-stop", _info => topbar.hide());

window.addEventListener("phx:set-theme", () => {
  const html = document.documentElement;
  const theme = html.getAttribute("data-theme") === "dark" ? "light" : "dark";
  html.classList.add("theme-transition");
  html.setAttribute("data-theme", theme);
  localStorage.setItem("theme", theme);
  window.setTimeout(() => html.classList.remove("theme-transition"), 250);
});

// Pushed by GT.Web.Base.LiveView when this page's assets are older than the server's.
window.addEventListener("phx:gt:new-version", () => {
  const el = document.getElementById("new-version");
  if (el) liveSocket.execJS(el, el.dataset.show);
});
window.addEventListener("gt:reload", () => window.location.reload());

liveSocket.connect();

// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (import.meta.env.DEV) {
  liveSocket.enableDebug();

  window.addEventListener("phx:live_reload:attached", ({ detail: reloader }) => {
    reloader.enableServerLogs();

    // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
    //
    //   * click with "c" key pressed to open at caller location
    //   * click with "d" key pressed to open at function component definition location
    let keyDown;
    window.addEventListener("keydown", e => (keyDown = e.key));
    window.addEventListener("keyup", e => (keyDown = null));
    window.addEventListener(
      "click",
      e => {
        if (keyDown === "c") {
          e.preventDefault();
          e.stopImmediatePropagation();
          reloader.openEditorAtCaller(e.target);
        } else if (keyDown === "d") {
          e.preventDefault();
          e.stopImmediatePropagation();
          reloader.openEditorAtDef(e.target);
        }
      },
      true,
    );

    window.liveReloader = reloader;
  });
}

// Sentry Toolbar: loaded via CDN because @sentry/toolbar on npm is a React hook
// and this app has no React. Runs on all envs except production and test: in test, the
// script and its sentry.io iframe hold back the page's load event that browser tests wait on.
const appEnv = window.ENV?.app_env;
if (appEnv && appEnv !== "production" && appEnv !== "test") {
  const script = document.createElement("script");
  script.src = "https://browser.sentry-cdn.com/sentry-toolbar/latest/toolbar.min.js";
  script.crossOrigin = "anonymous";
  script.referrerPolicy = "origin";
  script.addEventListener("load", () => {
    window.SentryToolbar?.init({
      organizationSlug: "gat",
      projectIdOrSlug: window.ENV.sentry_project,
    });
  });
  document.body.appendChild(script);
}
